import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType, ServerInfo } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { GAME_TYPE_META } from '../../game-types';

/** Sentinel region ID meaning "auto-pick by lowest ping". */
const CLOSEST_REGION_ID = 'closest';

@customElement('logon-panel')
export class LogonPanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        pointer-events: auto;
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        /* Primary mobile-lobby flow (name, dropdowns, PLAY) — the :active rules
           give press feedback, so drop the grey tap flash. Inherited, so it
           covers the dropdowns, menu items, and buttons. */
        -webkit-tap-highlight-color: transparent;
      }

      /* --content-w is the shared inner row width used by input, dropdown
         row, AND the dropdown popup menus so they all line up at any vw. */
      .logon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(400px, 92vw);
        --content-w: min(330px, calc(92vw - 32px));
        --region-w: 130px;
        --row-gap: 10px;
        --dropdown-h: 44px;
        /* Enables the @container query below that stacks the dropdowns
           when the panel is too narrow for them side-by-side. */
        container-type: inline-size;
        box-sizing: border-box;
        padding: 28px 16px 24px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        /* Legacy's #logon black, lighter than the shared panel token (0.9) so
           the map shows through. Used nowhere else, so it stays inline. */
        background-color: rgba(0, 0, 0, 0.75);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
      }

      /* Tighter padding + gap when vertical room is short (landscape phones
         and short browser windows). Mirrors the "pull to edge" rule of the
         lobby's top row and lobby-footer. Reclaims ~24px of vertical space
         inside the panel for the dropdown menu to open into. */
      @media (max-height: 500px) {
        .logon {
          padding: 14px 12px 14px;
          gap: 10px;
        }
      }

      .logo {
        width: min(208px, 60%);
        aspect-ratio: 208 / 60;
        background-image: url('/vanilla/ui/logo.png');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
        cursor: default;
        flex-shrink: 0;
      }

      .playername {
        display: block;
        font-size: var(--font-size-lg);
        font-family: var(--font-family);
        width: var(--content-w);
        padding-left: 14px;
        background-color: var(--color-bg-input);
        color: var(--color-text-primary);
        border: none;
        border-bottom: 2px solid var(--color-border-dark);
        box-shadow: var(--shadow-input);
        border-radius: var(--radius-md);
        box-sizing: border-box;
        height: var(--dropdown-h);
        /* The host turns selection off for the panel; typed text keeps it. */
        -webkit-user-select: text;
        user-select: text;
      }

      @media (hover: hover) {
        .playername:hover {
          background-color: var(--color-bg-hover);
        }
      }

      .playername:focus {
        outline: none;
        background-color: var(--color-bg-hover);
      }

      .playoptions {
        position: relative;
        height: var(--dropdown-h);
        width: var(--content-w);
        display: flex;
        gap: var(--row-gap);
      }

      .dropdown {
        position: relative;
        height: var(--dropdown-h);
        border-radius: var(--radius-md);
        border-bottom: 2px solid var(--color-border-dark);
        background-color: var(--color-bg-input);
        color: var(--color-text-primary);
        box-sizing: border-box;
        box-shadow: var(--shadow-input);
        cursor: pointer;
      }

      @media (hover: hover) {
        .dropdown:hover {
          background-color: var(--color-bg-hover);
        }

        .dropdown:hover .arrow {
          border-right-color: var(--color-text-primary);
          border-bottom-color: var(--color-text-primary);
        }

        .dropdown:hover .label {
          color: var(--color-text-primary);
        }
      }

      /* Press feedback on touch — clears the moment finger releases. */
      .dropdown:active {
        background-color: var(--color-bg-hover);
      }

      .dropdown.region {
        flex: 0 0 var(--region-w);
      }

      .dropdown.game {
        flex: 1 1 auto;
        min-width: 0; /* allows flex item to shrink below content width */
      }

      /* <button> with all native styling reset so it occupies the dropdown
         box identically to the prior div. Buttons give us Enter/Space toggle
         and screen-reader announcements for free. */
      .dropdown-header {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: transparent;
        border: none;
        padding: 0;
        margin: 0;
        font: inherit;
        color: inherit;
        text-align: left;
        cursor: pointer;
      }

      .arrow {
        position: absolute;
        top: 14px;
        left: 12px;
        border-right: 4px solid var(--color-text-secondary);
        border-bottom: 4px solid var(--color-text-secondary);
        height: 7px;
        width: 7px;
        transform: rotate(45deg);
      }

      /* right:8px bounds label/value to the dropdown box; without it the
         text spills past the right edge at narrow widths because the box
         shrinks but the text keeps its natural width. */
      .label {
        position: absolute;
        top: 6px;
        left: 36px;
        right: 8px;
        font-size: var(--font-size-sm);
        font-weight: bold;
        color: var(--color-text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .value {
        position: absolute;
        top: 20px;
        left: 36px;
        right: 8px;
        font-size: var(--font-size-md);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Cap height so the menu fits inside the viewport. lobby-footer is
         hidden while a dropdown is open (sibling-selector rule in
         airmash-lobby), so we don't need to reserve footer space — the
         budget is just (100vh - menu-top) minus a small bottom breathing
         margin. menu-top is roughly 50vh + 56px (panel center + dropdown
         row below center), so 50dvh - 64px is the budget. vh fallback for
         pre-2022 browsers. */
      .dropdown-menu {
        position: absolute;
        top: calc(100% + 2px);
        left: 0;
        background-color: var(--color-bg-input);
        border-radius: var(--radius-md);
        border-bottom: 2px solid var(--color-border-dark);
        box-shadow: var(--shadow-input);
        z-index: 10;
        max-height: min(calc(50vh - 64px), 400px);
        max-height: min(calc(50dvh - 64px), 400px);
        overflow-y: auto;
        overscroll-behavior: contain;
        /* Breathing room before the menu's bottom border. */
        padding-bottom: 8px;
      }

      .dropdown.region .dropdown-menu,
      .dropdown.game .dropdown-menu {
        width: var(--content-w);
      }

      /* Game menu aligns with the row's left edge — shift back by the
         region column width plus the inter-column gap. */
      .dropdown.game .dropdown-menu {
        left: calc(-1 * (var(--region-w) + var(--row-gap)));
      }

      /* Stack the region+game dropdowns vertically once the panel is too
         narrow to keep them side-by-side without squashing game. ~360px
         container width = roughly iPhone SE 2/3 portrait (375vw). */
      @container (max-width: 360px) {
        .playoptions {
          flex-direction: column;
          height: auto;
          gap: 8px;
        }

        .dropdown.region,
        .dropdown.game {
          flex: 0 0 var(--dropdown-h);
          width: var(--content-w);
        }

        .dropdown.game .dropdown-menu {
          left: 0;
        }
      }

      /* Identical grid template per row keeps the count columns vertically
         aligned regardless of name length; minmax(0, 1fr) lets the name
         shrink so the right-side columns stay anchored, with ellipsis on
         the name when it can't fit. */
      .region-menu-item {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 70px 50px 70px;
        align-items: center;
        margin: 0 8px;
      }

      .region-menu-item.selectable {
        cursor: pointer;
      }

      @media (hover: hover) {
        .region-menu-item.selectable:hover {
          background-color: var(--color-bg-item-hover);
        }

        .region-menu-item.selectable:hover .region-name,
        .region-menu-item.selectable:hover .region-humans,
        .region-menu-item.selectable:hover .region-bots,
        .region-menu-item.selectable:hover .region-ping,
        .region-menu-item.selectable:hover .ms {
          color: var(--color-text-white);
        }
      }

      .region-menu-item.selectable:active {
        background-color: var(--color-bg-item-hover);
      }

      .region-menu-item.selected {
        background-color: var(--color-bg-selected);
      }

      .region-menu-item .region-name {
        padding: 11px 0 11px 10px;
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .region-menu-item .region-humans {
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        text-align: center;
      }

      .region-menu-item .region-bots {
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
        text-align: center;
      }

      .region-menu-item .region-ping {
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        text-align: center;
        padding-right: 10px;
      }

      .region-menu-item.header {
        margin-top: 4px;
        margin-bottom: 4px;
      }

      .region-menu-item.header .region-name,
      .region-menu-item.header .region-humans,
      .region-menu-item.header .region-bots,
      .region-menu-item.header .region-ping {
        font-size: var(--font-size-sm);
        font-weight: bold;
        color: var(--color-text-secondary);
        padding: 4px 0 4px 0;
      }

      .region-menu-item.header .region-name {
        padding-left: 10px;
      }

      .region-menu-item .autoregion {
        margin-left: 4px;
        color: var(--color-text-tertiary);
      }

      .region-menu-item .ms {
        margin-left: 2px;
        color: var(--color-text-tertiary);
      }

      .region-menu-item .players-unknown {
        color: var(--color-text-muted);
      }

      /* Same grid pattern as region menu. The HUMANS column is sized for
         the bold header text — narrower values get visually clipped. */
      .game-menu-item {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 65px 35px;
        align-items: center;
        margin: 0 8px;
      }

      .game-menu-item.selectable {
        cursor: pointer;
      }

      @media (hover: hover) {
        .game-menu-item.selectable:hover {
          background-color: var(--color-bg-item-hover);
        }

        .game-menu-item.selectable:hover .game-name,
        .game-menu-item.selectable:hover .game-players,
        .game-menu-item.selectable:hover .game-bots {
          color: var(--color-text-white);
        }
      }

      .game-menu-item.selectable:active {
        background-color: var(--color-bg-item-hover);
      }

      .game-menu-item.selected {
        background-color: var(--color-bg-selected);
      }

      /* Loading / finding-closest message in place of an empty games list. */
      .game-menu-item.placeholder {
        display: block;
        padding: 12px 0;
        text-align: center;
        font-size: var(--font-size-13);
        color: var(--color-text-tertiary);
        font-style: italic;
        cursor: default;
      }

      .game-menu-item .game-name {
        padding: 11px 0 11px 10px;
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .game-menu-item .game-players {
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        text-align: center;
        margin-right: 8px;
      }

      .game-menu-item .game-bots {
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
        text-align: center;
        margin-right: 4px;
      }

      .game-menu-item.header {
        margin-top: 4px;
        margin-bottom: 4px;
      }

      .game-menu-item.header .game-name,
      .game-menu-item.header .game-players,
      .game-menu-item.header .game-bots {
        font-size: var(--font-size-sm);
        font-weight: bold;
        color: var(--color-text-secondary);
        padding: 4px 0 4px 0;
      }

      .game-menu-item.header .game-name {
        padding-left: 10px;
      }

      .game-menu-item.type-header .game-name {
        font-weight: bold;
      }

      /* Info-icon is hover-only — hide on touch-primary where there's no
         way to trigger the tooltip. */
      .game-menu-item .info-container {
        display: inline-block;
        margin-left: 6px;
      }

      @media (hover: none) {
        .game-menu-item .info-container {
          display: none;
        }
      }

      .game-menu-item .info-icon {
        display: inline-block;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background-color: var(--color-bg-item-hover);
        color: var(--color-text-primary);
        font-size: var(--font-size-xxs);
        text-align: center;
        line-height: 14px;
        cursor: help;
        opacity: 0.5;
      }

      @media (hover: hover) {
        .game-menu-item .info-icon:hover {
          opacity: 1;
        }
      }

      /* Tooltip is position:fixed and rendered at :host level (sibling of
         .logon) so the .game-name's overflow:hidden and .dropdown-menu's
         overflow-y:auto can't clip it. Coords are computed from the icon's
         bounding rect on mouseenter. */
      .info-tooltip {
        position: fixed;
        z-index: 1000;
        width: 240px;
        padding: 8px 10px;
        background-color: var(--color-bg-tooltip);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-sm);
        font-weight: normal;
        line-height: 1.4;
        color: var(--color-text-tooltip);
        white-space: normal;
        box-sizing: border-box;
        pointer-events: none;
      }

      .info-tooltip .arrow {
        position: absolute;
        top: 15px;
        left: -6px;
        width: 0;
        height: 0;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 6px solid var(--color-bg-tooltip);
      }

      .game-menu-item .players-unknown {
        color: var(--color-text-muted);
      }

      .buttons {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 10px;
        margin-top: 4px;
      }

      /* Hide the buttons row while a dropdown is open so its green doesn't
         peek out below the menu when the menu's max-height doesn't quite
         clear the play button (happens on landscape phones where the menu
         budget is tight). visibility (not display:none) preserves layout. */
      :host([dropdown-open]) .buttons {
        visibility: hidden;
      }

      .btn {
        padding: 12px 20px;
        min-height: 44px; /* iOS HIG touch target */
        border-radius: var(--radius-sm);
        font-size: var(--font-size-16);
        font-weight: bold;
        font-family: var(--font-family);
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border: none;
        border-bottom: 2px solid var(--color-border-soft);
        box-sizing: border-box;
      }

      .btn:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
      }

      .btn-play {
        background-color: var(--color-btn-play);
      }

      @media (hover: hover) {
        .btn-play:hover {
          background-color: var(--color-btn-play-hover);
          box-shadow: var(--shadow-btn-hover);
        }
      }

      .btn-play:disabled {
        background-color: var(--color-btn-disabled);
        cursor: not-allowed;
      }

      .btn-login {
        background-color: var(--color-btn-login);
      }

      @media (hover: hover) {
        .btn-login:hover:not(:disabled) {
          background-color: var(--color-btn-login-hover);
          box-shadow: var(--shadow-btn-hover);
        }
      }

      .btn-login:disabled {
        background-color: var(--color-btn-disabled);
        cursor: not-allowed;
      }

      /*
       * SIGN IN currently sits disabled with a title-tooltip explaining why
       * (see NOTES.md 2026-05-30 — disabled until we're hosted on a trusted
       * domain). On touch the title doesn't fire without hover, so the
       * button looks dead and confusing. Hide it on touch while it's
       * disabled; PLAY AS GUEST stays the single visible path. Self-cleaning:
       * when sign-in is re-enabled, :disabled stops matching and the button
       * appears on touch again with no code change needed here.
       */
      @media (hover: none) {
        .btn-login:disabled {
          display: none;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @state() private selectedRegionId: string | null = CLOSEST_REGION_ID;
  @state() private selectedGameType: string | null = null;
  @state() private regionDropdownOpen = false;
  @state() private gameDropdownOpen = false;

  /** Hover-tooltip state — typeNum is the game-type currently hovered, or
   *  null when no tooltip is showing. Tooltip is position:fixed and rendered
   *  at :host level so it escapes .game-name's overflow:hidden and the
   *  dropdown-menu's overflow-y:auto. */
  @state() private tooltipTypeNum: number | null = null;
  @state() private tooltipLeft = 0;
  @state() private tooltipTop = 0;

  /** Reflected so airmash-lobby's CSS can hide the lobby-footer (via sibling
   *  selector) while either dropdown is open — keeps the footer from
   *  overlapping the menu visually on portrait. Derived in willUpdate. */
  @property({ type: Boolean, reflect: true, attribute: 'dropdown-open' }) dropdownOpen = false;

  /** One-shot: consume a URL invite the first time uiState.invite lands. */
  private hasAppliedInvite = false;
  /** Latches once we've honoured `uiState.savedRegion`. Moved out of
   *  firstUpdated() because setting `@state` there fires an extra render
   *  cycle — Lit warns about "Element scheduled an update after an update
   *  completed". willUpdate runs the same logic before the first render so
   *  the assignment is part of the natural update flow. */
  private hasAppliedSavedRegion = false;

  @query('.playername') private nameInput!: HTMLInputElement;

  /** Close any open dropdown on a click anywhere else. The dropdown headers
   *  and menus stop their own clicks, so every click that gets here is
   *  outside them — the rest of the panel included. */
  private handleDocumentClick = () => {
    this.regionDropdownOpen = false;
    this.gameDropdownOpen = false;
  };

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.nameInput) {
      gsap.killTweensOf(this.nameInput);
    }
  }

  override willUpdate() {
    this.dropdownOpen = this.regionDropdownOpen || this.gameDropdownOpen;

    // The info-icons live inside the game menu, so a tooltip can't outlive it.
    // Clear it whenever the menu is closed — covers outside-click, region
    // toggle, and selecting an item by clicking the icon's row (where the
    // icon's mouseleave may never fire because it unmounts under the pointer).
    if (!this.gameDropdownOpen && this.tooltipTypeNum !== null) {
      this.tooltipTypeNum = null;
    }

    // First render only — honour the saved region from the previous session.
    // Done here rather than firstUpdated() to avoid a double-render cycle:
    // willUpdate runs before the render, so setting state is part of the
    // same cycle. uiState.savedRegion is loaded synchronously by toolsLoad-
    // Settings() during boot, well before any logon-panel instance exists.
    if (!this.hasAppliedSavedRegion) {
      if (this.uiState.savedRegion) {
        this.selectedRegionId = this.uiState.savedRegion;
      }
      this.hasAppliedSavedRegion = true;
    }

    // Honour a URL invite by pre-selecting its region + room. Sentinel so a
    // later user override (clicking a different region) doesn't get snapped
    // back. Can't run in firstUpdated — uiState.invite arrives later than
    // first render: gamesDataService.start() is async, and Games.ts only
    // populates uiState.invite after regions load.
    if (!this.hasAppliedInvite && this.uiState.invite?.isValid) {
      this.selectedRegionId = this.uiState.invite.regionId;
      this.selectedGameType = this.uiState.invite.roomId;
      this.hasAppliedInvite = true;
    }

    // If a saved/selected region disappears server-side, the lobby would
    // otherwise sit on a phantom "Select" with no playable game. Fall back
    // to CLOSEST once regions have actually loaded — checking before that
    // would silently drop the user's saved region every boot, since
    // gamesDataService.start() is async.
    if (
      this.selectedRegionId !== null &&
      this.selectedRegionId !== CLOSEST_REGION_ID &&
      this.uiState.regions.length > 0 &&
      !this.uiState.regions.some((r) => r.id === this.selectedRegionId)
    ) {
      this.selectedRegionId = CLOSEST_REGION_ID;
    }

    // Pick a game type when either (a) we don't have one yet, or (b) the
    // current one no longer exists in the active region's game list — which
    // happens when the server reloads regions and our previous ID is gone.
    const region = this.resolvedRegion;
    if (!region || region.games.length === 0) return;

    const currentExists = this.selectedGameType && region.games.some((g) => g.id === this.selectedGameType);
    if (!currentExists) {
      this.selectedGameType = region.games[0]?.id ?? null;
    }
  }

  private handleNameInput(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) return;
    this.api.setPlayerName(e.target.value);
  }

  /** Enter in the name field starts the game, as legacy's keypress did. Stopped
   *  here: starting puts the game in CONNECTING and opens a new player's help at
   *  once, and the window's game key handler closes the help on any key but H.
   *  Legacy's keypress came after that handler had ignored the keydown. */
  private handleNameKeydown(e: KeyboardEvent) {
    // An IME confirming a word with Enter isn't a submit. Chromium flags it
    // isComposing; Safari sends it after compositionend as keyCode 229.
    if (e.key !== 'Enter' || e.isComposing || e.keyCode === 229) return;
    e.stopPropagation();
    this.handlePlay();
  }

  private toggleRegionDropdown(e: Event) {
    e.stopPropagation();
    this.regionDropdownOpen = !this.regionDropdownOpen;
    this.gameDropdownOpen = false;
    this.api.playClick();
  }

  private toggleGameDropdown(e: Event) {
    e.stopPropagation();
    this.gameDropdownOpen = !this.gameDropdownOpen;
    this.regionDropdownOpen = false;
    this.api.playClick();
  }

  private selectRegion(e: Event, regionId: string) {
    e.stopPropagation();
    this.selectedRegionId = regionId;
    this.regionDropdownOpen = false;
    this.api.playClick();
    // willUpdate will pick a fresh game type if our old one isn't in the new region.
    this.selectedGameType = null;
  }

  private selectGameType(e: Event, gameType: string) {
    e.stopPropagation();
    this.selectedGameType = gameType;
    this.gameDropdownOpen = false;
    this.api.playClick();
  }

  /** PLAY plays the UI click, as legacy's button did; Enter starts silently. */
  private handlePlayClick() {
    this.api.playClick();
    this.handlePlay();
  }

  private handlePlay() {
    const name = this.uiState.playerName.trim();
    if (name.length === 0) {
      this.highlightNameInput();
      return;
    }

    const region = this.resolvedRegion;
    if (!region || !this.selectedGameType) return;

    const gameServer = region.games.find((g) => g.id === this.selectedGameType);
    if (!gameServer) return;

    // regionId is where we're going; regionPreference is the user's *selection*,
    // kept as 'closest' verbatim so the next session restores the auto-pick
    // rather than pinning to today's choice.
    const server: ServerInfo = {
      host: gameServer.host,
      path: gameServer.path,
      roomId: gameServer.id,
      roomName: gameServer.nameShort ?? gameServer.name,
      regionId: region.id,
      regionName: region.name,
      regionPreference: this.selectedRegionId ?? region.id,
    };

    // The lobby is hidden, not removed, so a still-focused name field could
    // keep a phone keyboard up over the game.
    this.nameInput.blur();
    this.api.play(name, server);
  }

  /** Red-flash the name input to signal it's required when the user hits PLAY empty. */
  private highlightNameInput() {
    const input = this.nameInput;
    if (!input) return;

    input.focus();
    gsap.killTweensOf(input);

    const style = getComputedStyle(this);
    const errorBg = style.getPropertyValue('--color-input-error-bg').trim();
    const normalBg = style.getPropertyValue('--color-bg-input').trim();

    gsap.fromTo(
      input,
      { scale: 1.05, backgroundColor: errorBg },
      {
        scale: 1,
        backgroundColor: normalBg,
        duration: 0.5,
        ease: 'power2.out',
        // Hand the inline transform/background back to CSS once the flash ends;
        // otherwise GSAP's leftover inline background-color masks the :hover /
        // :focus rules on every subsequent interaction.
        clearProps: 'transform,backgroundColor',
      },
    );
  }

  /** The closest region (by ping), or null if not yet determined */
  private get closestRegion() {
    const { regions, closestRegionId } = this.uiState;
    return closestRegionId === null ? null : (regions.find((r) => r.id === closestRegionId) ?? null);
  }

  /** Resolves CLOSEST_REGION_ID to the actual region, or returns the selected region */
  private get resolvedRegion() {
    if (this.selectedRegionId === CLOSEST_REGION_ID) {
      return this.closestRegion;
    }
    return this.uiState.regions.find((r) => r.id === this.selectedRegionId);
  }

  private get selectedRegionDisplay() {
    if (this.selectedRegionId === CLOSEST_REGION_ID) {
      return 'Closest';
    }
    const region = this.uiState.regions.find((r) => r.id === this.selectedRegionId);
    return region?.name ?? 'Select';
  }

  private get gamesGroupedByType() {
    const region = this.resolvedRegion;
    if (!region) return [];

    const groups = new Map<number, typeof region.games>();
    for (const game of region.games) {
      const typeNum = parseInt(game.type, 10);
      const group = groups.get(typeNum);
      if (group) {
        group.push(game);
      } else {
        groups.set(typeNum, [game]);
      }
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([typeNum, games]) => ({
        typeNum,
        typeName: GAME_TYPE_META[typeNum]?.name ?? `Type ${typeNum}`,
        games,
      }));
  }

  /** Human message for an empty games list, so the dropdown shows *why* it's
   *  empty instead of a bare gap during the boot fetch / ping window. `null`
   *  once games are ready. Connect-safe: this is display-only; `resolvedRegion`
   *  (which drives PLAY) stays strict, so we never connect to a fallback. */
  private get gamesListPlaceholder(): string | null {
    if (this.gamesGroupedByType.length > 0) return null;
    if (this.uiState.regions.length === 0) return 'Loading servers…';
    // Regions are loaded but we're still pinging to pick the closest one.
    if (this.selectedRegionId === CLOSEST_REGION_ID && this.closestRegion === null) {
      return 'Finding closest server…';
    }
    return 'No games available';
  }

  private get selectedGameName() {
    const region = this.resolvedRegion;
    if (!region) return 'Select';

    const game = region.games.find((g) => g.id === this.selectedGameType);
    return game?.name ?? 'Select';
  }

  private get canPlay(): boolean {
    const region = this.resolvedRegion;
    if (!region || !this.selectedGameType) return false;
    // Ensure selected game still exists in the region
    return region.games.some((g) => g.id === this.selectedGameType);
  }

  override render() {
    return html`
      <div class="logon">
        <div class="logo"></div>

        <input
          class="playername"
          type="text"
          placeholder="Enter your name"
          aria-label="Player name"
          maxlength="20"
          autocomplete="off"
          spellcheck="false"
          enterkeyhint="go"
          .value=${this.uiState.playerName}
          @input=${this.handleNameInput}
          @keydown=${this.handleNameKeydown}
        />

        <div class="playoptions">
          <div class="dropdown region">
            <button
              type="button"
              class="dropdown-header"
              aria-haspopup="listbox"
              aria-expanded=${this.regionDropdownOpen}
              aria-label="Region: ${this.selectedRegionDisplay}"
              @click=${this.toggleRegionDropdown}
            >
              <div class="arrow"></div>
              <div class="label">REGION</div>
              <div class="value">${this.selectedRegionDisplay}</div>
            </button>
            ${this.regionDropdownOpen ? this.renderRegionMenu() : nothing}
          </div>

          <div class="dropdown game">
            <button
              type="button"
              class="dropdown-header"
              aria-haspopup="listbox"
              aria-expanded=${this.gameDropdownOpen}
              aria-label="Game: ${this.selectedGameName}"
              @click=${this.toggleGameDropdown}
            >
              <div class="arrow"></div>
              <div class="label">GAME</div>
              <div class="value">${this.selectedGameName}</div>
            </button>
            ${this.gameDropdownOpen ? this.renderGameMenu() : nothing}
          </div>
        </div>

        <div class="buttons">
          <button class="btn btn-play" ?disabled=${!this.canPlay} @click=${this.handlePlayClick}>
            ${this.uiState.isLoggedIn ? 'PLAY' : 'PLAY AS GUEST'}
          </button>
          ${
            this.uiState.isLoggedIn
              ? nothing
              : html`<button
                  class="btn btn-login"
                  disabled
                  title="Sign-in is temporarily disabled until we're hosted on a trusted domain. Play as guest in the meantime."
                >
                  SIGN IN
                </button>`
          }
        </div>
      </div>
      ${this.renderTooltip()}
    `;
  }

  /** Description tooltip — rendered at :host level (sibling of .logon) with
   *  position:fixed so neither .game-name's overflow:hidden nor the
   *  dropdown-menu's overflow:auto can clip it. */
  private renderTooltip() {
    if (this.tooltipTypeNum === null) return nothing;
    const desc = GAME_TYPE_META[this.tooltipTypeNum]?.description;
    if (!desc) return nothing;
    return html`
      <div
        class="info-tooltip"
        role="tooltip"
        style=${styleMap({ left: `${this.tooltipLeft}px`, top: `${this.tooltipTop}px` })}
      >
        <div class="arrow"></div>
        ${desc}
      </div>
    `;
  }

  private getRegionCounts(region: (typeof this.uiState.regions)[number]): { humans: number | null; bots: number } {
    let players = 0;
    let bots = 0;
    let hasPlayers = false;

    for (const game of region.games) {
      if (game.players != null) {
        players += game.players;
        hasPlayers = true;
      }
      if (game.bots != null) {
        bots += game.bots;
      }
    }

    // Humans = players - bots (clamped to 0 in case of bad server data)
    const humans = hasPlayers ? Math.max(0, players - bots) : null;
    return { humans, bots };
  }

  private renderRegionMenu() {
    const closestRegionName = this.closestRegion?.name;

    return html`
      <div class="dropdown-menu" role="listbox" aria-label="Region" @click=${(e: Event) => e.stopPropagation()}>
        <div class="region-menu-item header" aria-hidden="true">
          <div class="region-name">REGION</div>
          <div class="region-humans">HUMANS</div>
          <div class="region-bots">BOTS</div>
          <div class="region-ping">PING</div>
        </div>

        <div
          class="region-menu-item selectable ${this.selectedRegionId === CLOSEST_REGION_ID ? 'selected' : ''}"
          role="option"
          aria-selected=${this.selectedRegionId === CLOSEST_REGION_ID}
          @click=${(e: Event) => this.selectRegion(e, CLOSEST_REGION_ID)}
        >
          <div class="region-name">
            Closest${closestRegionName ? html`<span class="autoregion">(${closestRegionName})</span>` : nothing}
          </div>
        </div>

        ${this.uiState.regions.map((region) => {
          const { humans, bots } = this.getRegionCounts(region);
          const selected = region.id === this.selectedRegionId;
          return html`
            <div
              class="region-menu-item selectable ${selected ? 'selected' : ''}"
              role="option"
              aria-selected=${selected}
              @click=${(e: Event) => this.selectRegion(e, region.id)}
            >
              <div class="region-name">${region.name}</div>
              <div class="region-humans">${humans != null ? humans : html`<span class="players-unknown">?</span>`}</div>
              <div class="region-bots">${bots}</div>
              <div class="region-ping">
                ${
                  region.ping != null
                    ? html`${region.ping}<span class="ms">ms</span>`
                    : html`<span class="players-unknown">?</span>`
                }
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderGameMenu() {
    const groupedGames = this.gamesGroupedByType;

    return html`
      <div class="dropdown-menu" role="listbox" aria-label="Game" @click=${(e: Event) => e.stopPropagation()}>
        <div class="game-menu-item header" aria-hidden="true">
          <div class="game-name">GAME</div>
          <div class="game-players">HUMANS</div>
          <div class="game-bots">BOTS</div>
        </div>

        ${
          groupedGames.length === 0
            ? html`<div class="game-menu-item placeholder" aria-disabled="true">${this.gamesListPlaceholder}</div>`
            : groupedGames.map(
                (group) => html`
                  <div
                    class="game-menu-item type-header selectable"
                    role="group"
                    aria-label=${group.typeName}
                    @click=${(e: Event) => this.selectFirstGameOfType(e, group.typeNum)}
                  >
                    <div class="game-name">
                      ${group.typeName}
                      <span class="info-container">
                        <span
                          class="info-icon"
                          data-type-num=${group.typeNum}
                          aria-label="${group.typeName} description"
                          @mouseenter=${this.handleInfoEnter}
                          @mouseleave=${this.handleInfoLeave}
                          >i</span
                        >
                      </span>
                    </div>
                  </div>

                  ${group.games.map((game) => {
                    const humans = game.players != null ? Math.max(0, game.players - (game.bots ?? 0)) : null;
                    const bots = game.bots ?? 0;
                    const selected = game.id === this.selectedGameType;
                    return html`
                      <div
                        class="game-menu-item selectable ${selected ? 'selected' : ''}"
                        role="option"
                        aria-selected=${selected}
                        @click=${(e: Event) => this.selectGameType(e, game.id)}
                      >
                        <div class="game-name">${game.nameShort ?? game.name}</div>
                        <div class="game-players">
                          ${humans != null ? humans : html`<span class="players-unknown">?</span>`}
                        </div>
                        <div class="game-bots">${bots}</div>
                      </div>
                    `;
                  })}
                `,
              )
        }
      </div>
    `;
  }

  private selectFirstGameOfType(e: Event, typeNum: number) {
    e.stopPropagation();
    const region = this.resolvedRegion;
    if (!region) return;

    const game = region.games.find((g) => parseInt(g.type, 10) === typeNum);
    if (game) {
      this.selectedGameType = game.id;
      this.gameDropdownOpen = false;
      this.api.playClick();
    }
  }

  /** Show the type-description tooltip beside the hovered info-icon. */
  private handleInfoEnter = (e: MouseEvent) => {
    const icon = e.currentTarget;
    if (!(icon instanceof HTMLElement)) return;
    const typeNum = Number(icon.dataset['typeNum']);
    if (!Number.isFinite(typeNum)) return;
    const rect = icon.getBoundingClientRect();
    this.tooltipTypeNum = typeNum;
    this.tooltipLeft = rect.right + 8;
    // -8 matches the existing visual offset where the arrow points at the
    // icon centre rather than its top.
    this.tooltipTop = rect.top - 8;
  };

  private handleInfoLeave = () => {
    this.tooltipTypeNum = null;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'logon-panel': LogonPanel;
  }
}
